import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { Matrix } from '@babylonjs/core/Maths/math.vector';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { VertexBuffer } from '@babylonjs/core/Meshes/buffer';
import { Geometry } from '@babylonjs/core/Meshes/geometry';
import { Scene } from '@babylonjs/core/scene';
import { DeepImmutable, FloatArray, IndicesArray, Nullable } from '@babylonjs/core/types';
import { AsyncZippable, strToU8, zip } from 'fflate';
import { SceneNode } from '../../../models/classes/scene-node';
/** Be careful when adding dependencies in this file. Importing and using a constructor function or a barrel file will balloon the size of the worker js bundle */

export type TypeIntArray = Exclude<IndicesArray, number[]>;
export type TypedArray = TypeIntArray | Float32Array | Float64Array;

export interface GeometryTransferrable {
    payload: GeometryConversionPayload;
    transfer: ArrayBuffer[];
}

export interface GeometryConversionPayload {
    options: USDOpts;
    id: string;
    indices: TypeIntArray;
    vertexBuffers: {
        [index: string]: Float32Array;
    };
}

export interface USDOpts {
    unitsPerMeter: number;
    xrAllowScaling: boolean;
}

export interface ConversionResult {
    file: Uint8Array;
}

export class USDZConverter {
    parse(scene: Scene, sceneNode: SceneNode, useWorker = false): Promise<Blob> {
        return Promise.reject();
    }

    convertGeometry(payload: GeometryConversionPayload) {
        const meshObject = this.buildGeometryFromBuffers(payload);
        return buildUSDAFile(payload.options, meshObject);
    }

    zipUSDZFiles(files: AsyncZippable) {
        return new Promise<Blob>((resolve, reject) => {
            zip(
                files,
                {
                    level: 0,
                },
                (err, res) => {
                    if (err) {
                        reject(err);
                    } else {
                        const blob = new Blob([res.buffer], { type: 'model/vnd.usdz+zip' });
                        resolve(blob);
                    }
                }
            );
        });
    }

    protected buildGeometryFromBuffers(payload: GeometryConversionPayload) {
        const positions = payload.vertexBuffers[VertexBuffer.PositionKind];
        if (!positions) {
            throw 'No indices in geometry';
        }
        const vertexCount = Math.floor(positions.length / 3);

        return buildGeometryFromData(
            payload.indices,
            vertexCount,
            positions,
            payload.vertexBuffers[VertexBuffer.NormalKind],
            payload.vertexBuffers[VertexBuffer.UVKind]
        );
    }

    protected buildGeometryFromObject(geometry: Geometry) {
        const indices = geometry.getIndices();
        const vertexCount = geometry.getTotalVertices();
        const positions = geometry.getVerticesData(VertexBuffer.PositionKind);
        if (!indices) {
            throw 'No indices in geometry';
        }
        if (!positions) {
            throw 'No indices in geometry';
        }
        return buildGeometryFromData(
            indices,
            vertexCount,
            positions,
            geometry.getVerticesData(VertexBuffer.NormalKind),
            geometry.getVerticesData(VertexBuffer.UVKind)
        );
    }
}

export const PRECISION = 7;

export function buildHeader(opts: USDOpts) {
    return `#usda 1.0
(
    customLayerData = {
        string creator = "KB3D USDZConverter"
    }
    metersPerUnit = ${1 / opts.unitsPerMeter}
    upAxis = "Y"
)
`;
}

export function buildUSDAFile(opts: USDOpts, dataToInsert: string) {
    let output = buildHeader(opts);
    output += dataToInsert;
    return strToU8(output);
}

// Xform
export function buildXform(object: AbstractMesh, geometry: Geometry, material: PBRMaterial, appendFileName = '') {
    const name = 'Object__' + escapeGUID(object.id) + appendFileName;
    const worldMatrix = object.computeWorldMatrix(true);
    const transform = buildMatrix(worldMatrix);

    if (worldMatrix.determinant() < 0) {
        console.warn('USDZConverter: USDZ does not support negative scales', object);
    }

    return `def Xform "${name}" (
    prepend references = @./geometries/Geometry__${escapeGUID(geometry.id)}${appendFileName}.usd@</Geometry>
)
{
    matrix4d xformOp:transform = ${transform}
    uniform token[] xformOpOrder = ["xformOp:transform"]
    rel material:binding = </Materials/Material__${escapeGUID(material.id)}>
}
`;
}

function buildMatrix(matrix: Matrix) {
    const array = matrix.toArray();
    return `( ${buildMatrixRow(array, 0)}, ${buildMatrixRow(array, 4)}, ${buildMatrixRow(array, 8)}, ${buildMatrixRow(
        array,
        12
    )} )`;
}

function buildMatrixRow(array: DeepImmutable<Float32Array | number[]>, offset: number) {
    return `(${array[offset + 0]}, ${array[offset + 1]}, ${array[offset + 2]}, ${array[offset + 3]})`;
} // Mesh

function buildGeometryFromData(
    indices: IndicesArray,
    verticesCount: number,
    positions: number[] | Float32Array,
    normals: Nullable<number[] | Float32Array>,
    uvs: Nullable<number[] | Float32Array>
) {
    const vertexCounts = Array(Math.floor(indices.length / 3))
        .fill(3)
        .join(', ');

    const name = 'Geometry';

    return `
def "Geometry"
{
    def Mesh "${name}"
    {
        int[] faceVertexCounts = [${vertexCounts}]
        int[] faceVertexIndices = [${stringifyIntArray(indices)}]
        normal3f[] normals = [${buildVector3Array(normals, verticesCount)}] (
            interpolation = "vertex"
        )
        point3f[] points = [${buildVector3Array(positions, verticesCount)}]
        float2[] primvars:st = [${buildVector2Array(uvs, verticesCount)}] (
            interpolation = "vertex"
        )
        uniform token subdivisionScheme = "none"
        uniform token orientation = "leftHanded"
    }
}
`;
}

function buildVector3Array(data: Nullable<number[] | Float32Array>, count: number) {
    let res = '';
    if (!data) {
        console.warn('USDZConverter: Data missing.');
        for (let i = 0; i < count; i++) {
            res += '(0, 0, 0), ';
        }
        return res;
    }

    return stringifyFloatArray(data, count, 3);
}

function buildVector2Array(data: Nullable<number[] | Float32Array>, count: number) {
    let res = '';
    if (!data) {
        console.warn('USDZConverter: Data missing.');
        for (let i = 0; i < count; i++) {
            res += '(0, 0), ';
        }
        return res;
    }

    return stringifyFloatArray(data, count, 2);
}

function stringifyIntArray(data: IndicesArray) {
    const count = data.length;
    let res = '';
    for (let i = 0; i < count; i++) {
        res += `${data[i]}, `;
    }

    return res.slice(0, -2);
}

function stringifyFloatArray(data: FloatArray, count: number, perStruc: number) {
    let res = '';
    if (perStruc === 2) {
        for (let i = 0; i < count; i++) {
            res += `(${data[i * 2].toPrecision(PRECISION)}, ${data[i * 2 + 1].toPrecision(PRECISION)}), `;
        }
    } else if (perStruc === 3) {
        for (let i = 0; i < count; i++) {
            res += `(${data[i * 3].toPrecision(PRECISION)}, ${data[i * 3 + 1].toPrecision(PRECISION)}, ${data[
                i * 3 + 2
            ].toPrecision(PRECISION)}), `;
        }
    } else {
        throw Error('Not supported');
    }
    return res.slice(0, -2);
}

export function escapeGUID(guid: string) {
    return guid.replace(/[-\s]+/g, '_');
}
